import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";

// Popup that shows the ECG images a patient's phone uploaded (ported from the
// web6 verifier dashboard's EcgStripsModal). Served by the same server as the
// report API:
//   GET androidapi/getEcgStrips?userid=<id>       -> { ok, strips: [{ id, uploaded_ms, duplicated_from }] }
//   GET androidapi/getEcgStripImage?id=<strip id> -> image bytes
// The dashboard looks strips up by the 6-digit device id, while the report page
// has used the long patient id, so `userids` lists every id to try, in order,
// until one returns strips.
export default function EcgStripsModal({ userids = [], onClose }) {
  const [strips, setStrips] = useState(null); // null = loading, [] = none
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(null); // src of the image shown full-size

  const idsKey = userids.filter(Boolean).join("|");

  useEffect(() => {
    const ids = idsKey ? idsKey.split("|") : [];
    if (!ids.length) {
      setStrips([]);
      return undefined;
    }
    let cancelled = false;
    setStrips(null);
    setError("");
    setZoom(null);
    (async () => {
      let found = [];
      let lastError = "";
      for (const id of ids) {
        try {
          const res = await axios.get(
            `${API_ENDPOINT}/androidapi/getEcgStrips?userid=${encodeURIComponent(id)}`
          );
          const list = (res.data && res.data.strips) || [];
          if (list.length) {
            found = list;
            break;
          }
        } catch (e) {
          lastError =
            (e.response && e.response.data && e.response.data.error) ||
            e.message ||
            "Failed to load images";
        }
      }
      if (cancelled) return;
      if (!found.length && lastError) setError(lastError);
      setStrips(found);
    })();
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  const fmtWhen = (s) => {
    const t = s.uploaded_ms;
    if (!t) return "";
    try {
      return new Date(Number(t)).toLocaleString();
    } catch (e) {
      return String(t);
    }
  };

  return (
    <div onClick={onClose} style={styles.overlay}>
      <div onClick={(e) => e.stopPropagation()} style={styles.panel}>
        <div style={styles.header}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#24333e" }}>
            Uploaded ECG images{strips ? " (" + strips.length + ")" : ""}
          </div>
          <button type="button" onClick={onClose} style={styles.closeBtn} title="Close">
            ✕
          </button>
        </div>

        {strips === null ? (
          <div style={styles.msg}>Loading images…</div>
        ) : error ? (
          <div style={{ ...styles.msg, color: "#e74c3c" }}>{error}</div>
        ) : !strips.length ? (
          <div style={styles.msg}>No images uploaded yet.</div>
        ) : (
          <div style={styles.grid}>
            {strips.map((s) => {
              const src = `${API_ENDPOINT}/androidapi/getEcgStripImage?id=${encodeURIComponent(s.id)}`;
              return (
                <div
                  key={s.id}
                  style={styles.card}
                  onClick={() => setZoom(src)}
                  title="Click to enlarge"
                >
                  <img src={src} alt="ECG upload" style={styles.thumb} />
                  <div style={styles.cap}>
                    {fmtWhen(s) || " "}
                    {s.duplicated_from ? (
                      <span style={styles.dupTag} title="Duplicated strip">
                        copy
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {zoom && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setZoom(null);
          }}
          style={styles.lightbox}
        >
          <img
            src={zoom}
            alt="ECG upload full size"
            style={{
              maxWidth: "94vw",
              maxHeight: "94vh",
              borderRadius: 8,
              boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
              background: "#fff",
            }}
          />
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    background: "#fff",
    borderRadius: 10,
    padding: "16px 18px",
    width: 720,
    maxWidth: "94vw",
    maxHeight: "86vh",
    overflowY: "auto",
    boxShadow: "0 12px 48px rgba(0,0,0,0.3)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#888",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
  },
  msg: { fontSize: 13, color: "#777", padding: "24px 4px", textAlign: "center" },
  grid: { display: "flex", flexWrap: "wrap", gap: 12 },
  card: {
    width: 200,
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
    background: "#f8fafc",
  },
  thumb: { width: "100%", display: "block", background: "#0f172a" },
  cap: { fontSize: 11, color: "#64748b", padding: "4px 6px", display: "flex", alignItems: "center", gap: 6 },
  dupTag: {
    fontSize: 9,
    fontWeight: 700,
    color: "#b45309",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    borderRadius: 4,
    padding: "0 4px",
  },
  lightbox: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    zIndex: 10001,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "zoom-out",
  },
};
